// Frontend service for usage metering endpoints
import { auth, FUNCTIONS_REGION } from "@/firebase";

// Lightweight in-memory cache to dedupe GET requests across panels and rapid re-renders
const INFLIGHT = new Map(); // key -> Promise
const CACHE = new Map(); // key -> { ts, data }
const TTL_MS = 15_000; // 15s is enough to cover initial page load without staleness issues

function mkKey(name, params) {
  try {
    return `${name}:${JSON.stringify(params || {})}`;
  } catch (_) {
    return `${name}:${String(params)}`;
  }
}

async function withCache(name, params, fetcher) {
  const key = mkKey(name, params);
  const now = Date.now();
  const cached = CACHE.get(key);
  if (cached && now - cached.ts < TTL_MS) {
    return cached.data;
  }
  if (INFLIGHT.has(key)) {
    return INFLIGHT.get(key);
  }
  const p = (async () => {
    try {
      const data = await fetcher();
      CACHE.set(key, { ts: Date.now(), data });
      return data;
    } finally {
      INFLIGHT.delete(key);
    }
  })();
  INFLIGHT.set(key, p);
  return p;
}

/**
 * Clear cached GET responses. Pass a prefix (e.g. "listOrgsUsage") to clear
 * only matching keys, or omit to clear everything.
 */
export function bustUsageCache(prefix) {
  if (!prefix) {
    CACHE.clear();
    return;
  }
  for (const key of CACHE.keys()) {
    if (key.startsWith(prefix)) CACHE.delete(key);
  }
}

const isDev = process.env.NODE_ENV !== "production";

function wrapCorsAware(error, endpointName) {
  // Browser CORS/network failures commonly surface as TypeError: Failed to fetch
  const msg = (error && (error.message || String(error))) || "";
  const isLikelyCors =
    error?.name === "TypeError" || /Failed to fetch|NetworkError/i.test(msg);
  if (isDev && isLikelyCors) {
    const e = new Error(
      `Request blocked by browser CORS in development. This should work once deployed. Endpoint: ${endpointName}`
    );
    // Attach a code we can detect in UI
    e.code = "CORS_DEV_BLOCKED";
    return e;
  }
  return error;
}

function getFunctionsBaseUrl() {
  try {
    const projectId = auth.app?.options?.projectId || "unit-org";
    return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net`;
  } catch (e) {
    return `https://${FUNCTIONS_REGION}-unit-org.cloudfunctions.net`;
  }
}

async function getToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");
  return await user.getIdToken();
}

export async function fetchOrgDailyUsage(orgId) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("usageDaily", { orgId }, async () => {
    try {
      console.log("usageService.fetchOrgDailyUsage:request", { orgId, base });
      const res = await fetch(
        `${base}/usageDaily?orgId=${encodeURIComponent(orgId)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.fetchOrgDailyUsage:response", json);
      return json;
    } catch (err) {
      throw wrapCorsAware(err, "usageDaily");
    }
  });
}

export async function fetchTopActiveOrgs(limit = 20) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    const res = await fetch(`${base}/topActiveOrgs?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    throw wrapCorsAware(err, "topActiveOrgs");
  }
}

export async function fetchUsageHeadline(orgId) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("usageHeadline", { orgId }, async () => {
    try {
      const url = new URL(`${base}/usageHeadline`);
      url.searchParams.set("orgId", orgId);
      console.log("usageService.fetchUsageHeadline:request", url.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.fetchUsageHeadline:response", json);
      return json; // { headline, totals30d, totalsAll, lastUpdatedAt }
    } catch (err) {
      throw wrapCorsAware(err, "usageHeadline");
    }
  });
}

export async function fetchOrgUsageTotals(orgId, days = null) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("usageTotals", { orgId, days }, async () => {
    try {
      const url = new URL(`${base}/usageTotals`);
      url.searchParams.set("orgId", orgId);
      if (days != null) url.searchParams.set("days", String(days));
      console.log("usageService.fetchOrgUsageTotals:request", url.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.fetchOrgUsageTotals:response", json);
      return json; // { totals, approxCostUSD, approxCostEUR }
    } catch (err) {
      throw wrapCorsAware(err, "usageTotals");
    }
  });
}

export async function fetchOrgUsageSeries(orgId, days = 14) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("usageSeries", { orgId, days }, async () => {
    try {
      const url = new URL(`${base}/usageSeries`);
      url.searchParams.set("orgId", orgId);
      if (days != null) url.searchParams.set("days", String(days));
      console.log("usageService.fetchOrgUsageSeries:request", url.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.fetchOrgUsageSeries:response", json);
      return json; // { days: [...] }
    } catch (err) {
      throw wrapCorsAware(err, "usageSeries");
    }
  });
}

// ----- API Keys CRUD -----
export async function createApiKey(orgId, name) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    console.log("usageService.createApiKey:request", { orgId, name });
    const res = await fetch(`${base}/createApiKey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgId, name }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.createApiKey:response", json);
    return json; // { keyId, name, secret, prefix, last4 }
  } catch (err) {
    throw wrapCorsAware(err, "createApiKey");
  }
}

export async function listApiKeys(orgId) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("listApiKeys", { orgId }, async () => {
    try {
      console.log("usageService.listApiKeys:request", { orgId });
      const res = await fetch(
        `${base}/listApiKeys?orgId=${encodeURIComponent(orgId)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.listApiKeys:response", json);
      return json; // { keys }
    } catch (err) {
      throw wrapCorsAware(err, "listApiKeys");
    }
  });
}

export async function renameApiKey(orgId, keyId, newName) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    console.log("usageService.renameApiKey:request", { orgId, keyId, newName });
    const res = await fetch(`${base}/renameApiKey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgId, keyId, newName }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.renameApiKey:response", json);
    return json;
  } catch (err) {
    throw wrapCorsAware(err, "renameApiKey");
  }
}

export async function deleteApiKey(orgId, keyId) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    console.log("usageService.deleteApiKey:request", { orgId, keyId });
    const res = await fetch(`${base}/deleteApiKey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgId, keyId }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.deleteApiKey:response", json);
    return json;
  } catch (err) {
    throw wrapCorsAware(err, "deleteApiKey");
  }
}

export async function activateApiKey(orgId, keyId) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    console.log("usageService.activateApiKey:request", { orgId, keyId });
    const res = await fetch(`${base}/activateApiKey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgId, keyId }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.activateApiKey:response", json);
    return json;
  } catch (err) {
    throw wrapCorsAware(err, "activateApiKey");
  }
}

// Hard delete API key
export async function purgeApiKey(orgId, keyId) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    console.log("usageService.purgeApiKey:request", { orgId, keyId });
    const res = await fetch(`${base}/purgeApiKey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgId, keyId }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.purgeApiKey:response", json);
    return json;
  } catch (err) {
    throw wrapCorsAware(err, "purgeApiKey");
  }
}

// Usage analytics
export async function fetchUsageBreakdown(orgId, days = null) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("usageBreakdownBySource", { orgId, days }, async () => {
    try {
      const url = new URL(`${base}/usageBreakdownBySource`);
      url.searchParams.set("orgId", orgId);
      if (days != null) url.searchParams.set("days", String(days));
      console.log("usageService.fetchUsageBreakdown:request", url.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.fetchUsageBreakdown:response", json);
      return json; // { bySource, byApiKey }
    } catch (err) {
      throw wrapCorsAware(err, "usageBreakdownBySource");
    }
  });
}

export async function fetchTopUsers(orgId, days = null, limit = 10) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  return withCache("usageTopUsers", { orgId, days, limit }, async () => {
    try {
      const url = new URL(`${base}/usageTopUsers`);
      url.searchParams.set("orgId", orgId);
      if (days != null) url.searchParams.set("days", String(days));
      if (limit != null) url.searchParams.set("limit", String(limit));
      console.log("usageService.fetchTopUsers:request", url.toString());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.fetchTopUsers:response", json);
      return json;
    } catch (err) {
      throw wrapCorsAware(err, "usageTopUsers");
    }
  });
}

// ----- Super Admin endpoints -----
export async function listOrgsUsage(options = {}) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  const fetcher = async () => {
    try {
      const url = `${base}/listOrgsUsage`;
      console.log("usageService.listOrgsUsage:request", url);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      console.log("usageService.listOrgsUsage:response", json);
      return json; // { orgs: [...] }
    } catch (err) {
      throw wrapCorsAware(err, "listOrgsUsage");
    }
  };
  if (options.force) {
    // Skip cache to reflect immediate backend changes
    return fetcher();
  }
  return withCache("listOrgsUsage", {}, fetcher);
}

export async function setDefaultFreeCap(defaultFreeCapEUR) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    const url = `${base}/setDefaultFreeCap`;
    console.log("usageService.setDefaultFreeCap:request", defaultFreeCapEUR);
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ defaultFreeCapEUR }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.setDefaultFreeCap:response", json);
    return json; // { ok: true, defaultFreeCapEUR }
  } catch (err) {
    throw wrapCorsAware(err, "setDefaultFreeCap");
  }
}

export async function setOrgExtraCredit(orgId, extraCreditEUR) {
  const token = await getToken();
  const base = getFunctionsBaseUrl();
  try {
    const url = `${base}/setOrgExtraCredit`;
    console.log("usageService.setOrgExtraCredit:request", {
      orgId,
      extraCreditEUR,
    });
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orgId, extraCreditEUR }),
    });
    if (!res.ok) throw new Error(await res.text());
    const json = await res.json();
    console.log("usageService.setOrgExtraCredit:response", json);
    return json; // { ok: true, orgId, extraCreditEUR }
  } catch (err) {
    throw wrapCorsAware(err, "setOrgExtraCredit");
  }
}
